import { superstructResolver } from '@hookform/resolvers/superstruct';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'react-toastify';

import { ButtonCancel, ButtonSave } from '@adflow/frontend/ui-buttons';
import { PasswordInput } from '@adflow/frontend/ui-form';
import { LoadingIndicator } from '@adflow/frontend/ui-graphics';
import { Modal } from '@adflow/frontend/ui-modal';
import {
	createErrorToast,
	createSuccessToast,
} from '@adflow/frontend/ui-toasts';
import {
	changePassword,
	useCheckResetPasswordTokenQuery,
	useMutationLogin,
	useRedirectFromWelcomePage,
} from '@adflow/frontend/util-auth';
import { ChangePasswordFormDto } from '@adflow/shared/util-auth';

const formSchema = ChangePasswordFormDto;
type formValues = ChangePasswordFormDto;

export const RESET_PASSWORD_MODAL_PARAM = 'reset-password';
export function useResetPasswordModalListener(
	setShowResetPasswordModal: Dispatch<SetStateAction<boolean>>
) {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	useEffect(() => {
		const shouldOpenModal =
			searchParams.get(RESET_PASSWORD_MODAL_PARAM) !== null;
		if (shouldOpenModal) {
			setShowResetPasswordModal(true);
			void navigate('/login', { replace: true });
		}
	}, [navigate, searchParams, setShowResetPasswordModal]);
}
const openRequestPasswordResetModalNavAction = {
	pathname: '/login',
	search: `?${RESET_PASSWORD_MODAL_PARAM}=t`,
};

export function ResetPassword() {
	const { t } = useTranslation(['login', 'user-form']);
	const navigate = useNavigate();

	const { mutateAsync: requestLogin } = useMutationLogin();

	const formMethods = useForm<formValues>({
		defaultValues: { confirmPassword: '', newPassword: '' },
		resolver: superstructResolver(formSchema, { coerce: true, mask: true }),
	});

	const { token = '' } = useParams();

	const navigateToLogin = useCallback(() => {
		void navigate(openRequestPasswordResetModalNavAction);
	}, [navigate]);

	const handleInvalidToken = useCallback(() => {
		createErrorToast(t('login:invalid password reset link'), {
			autoClose: false,
		});
		navigateToLogin();
	}, [navigateToLogin, t]);

	const {
		data: { isValidToken },
		error,
	} = useCheckResetPasswordTokenQuery(token);

	useEffect(() => {
		if (error || !isValidToken) {
			handleInvalidToken();
		}
	}, [isValidToken, handleInvalidToken, error]);

	const redirectFromWelcomePage = useRedirectFromWelcomePage();

	const handleChangePassword = formMethods.handleSubmit(async (data) => {
		try {
			const responseData = await changePassword({
				newPassword: data.newPassword,
				token,
			});
			const { email } = responseData;

			await requestLogin({
				email,
				password: data.newPassword,
			});
			// This is a hack, but I could not get anything else to work in a
			// reasonable amount of time. -Joshua
			redirectFromWelcomePage();
			toast.dismiss();
			createSuccessToast(
				t('login:Your password has been successfully changed'),
				10
			);
		} catch {
			handleInvalidToken();
		}
	});

	return isValidToken ? (
		<Modal
			show
			onRequestClose={navigateToLogin}
			dialogStyleOverrides={{ width: '340px' }}
		>
			<form onSubmit={handleChangePassword}>
				<Modal.Header>{t('user-form:Change password')}</Modal.Header>
				<Modal.Body>
					<FormProvider {...formMethods}>
						<PasswordInput
							name="newPassword"
							label={t('login:New password')}
							placeholder={t('login:Enter new password')}
							autoFocus
						/>

						<PasswordInput
							name="confirmPassword"
							label={t('login:Confirm password')}
							placeholder={t('login:Confirm password')}
							outerStyle={{ marginTop: '1rem' }}
						/>
					</FormProvider>
				</Modal.Body>
				<Modal.Footer>
					<ButtonCancel onClick={navigateToLogin} />
					<ButtonSave type="submit">
						{t('user-form:Change password')}
					</ButtonSave>
				</Modal.Footer>
			</form>
		</Modal>
	) : (
		<LoadingIndicator />
	);
}
